// src/components/configuration/sections/GBPSettings.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, MapPin, Star } from 'lucide-react';

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Spinner } from '../../ui/spinner';

// GBP hook
import { useGBP } from '../../../hooks/useGBP';

/**
 * GBP Settings Component - Handles OAuth flow and location selection
 *
 * IMPORTANT: This component handles OAuth redirects from Google
 * - On mount, checks for ?success=true&step=select_locations query params
 * - Multi-step flow: Disconnected → OAuth → Select Locations → Connected
 */
const GBPSettings = () => {
  const {
    // Connection status
    gbpConnection,
    gbpConnectionLoading,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Accounts & Locations
    accounts,
    accountsLoading,
    locations,
    locationsLoading,
    selectedLocations,
    savingLocations,
    connectedLocations,
    primaryLocation,
    accountName,

    // Disconnection
    disconnecting,

    // Operations
    loadGBPConnectionStatus,
    startGBPOAuth,
    fetchGBPAccounts,
    fetchGBPLocations,
    saveGBPLocations,
    setSelectedLocations,
    disconnectGBP
  } = useGBP();

  // Local UI state
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState('loading'); // loading, disconnected, select_locations, connected
  const [oauthError, setOauthError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // ================================
  // OAUTH REDIRECT HANDLING
  // ================================

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const errorMessage = urlParams.get('message');
    const step = urlParams.get('step');

    if (success === 'true') {
      console.log('✅ GBPSettings: OAuth success detected, step:', step);
      window.history.replaceState({}, document.title, window.location.pathname);
      if (step === 'select_locations') {
        setCurrentStep('select_locations');
        fetchGBPAccounts();
      }
    } else if (error) {
      console.error('❌ GBPSettings: OAuth error detected:', error, errorMessage);
      setOauthError(errorMessage || error);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchGBPAccounts]);

  // ================================
  // DETERMINE CURRENT STEP
  // ================================

  useEffect(() => {
    if (gbpConnectionLoading) {
      setCurrentStep('loading');
      return;
    }
    if (currentStep === 'select_locations') return;
    if (connected) {
      setCurrentStep('connected');
    } else {
      setCurrentStep('disconnected');
    }
  }, [connected, gbpConnectionLoading, currentStep]);

  // ================================
  // OAUTH URL GENERATED - REDIRECT
  // ================================

  useEffect(() => {
    if (oauthUrl) {
      console.log('🚀 GBPSettings: OAuth URL generated, redirecting to Google...');
      window.location.href = oauthUrl;
    }
  }, [oauthUrl]);

  // ================================
  // AUTO-SELECT ACCOUNT IF ONLY ONE
  // ================================

  useEffect(() => {
    if (accounts && accounts.length === 1 && !selectedAccount) {
      console.log('📋 GBPSettings: Only one account found, auto-selecting');
      setSelectedAccount(accounts[0]);
      fetchGBPLocations(accounts[0].name);
    }
  }, [accounts, selectedAccount, fetchGBPLocations]);

  // ================================
  // HANDLERS
  // ================================

  const handleStartOAuth = () => {
    setOauthError(null);
    startGBPOAuth();
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    fetchGBPLocations(account.name);
  };

  const handleLocationToggle = (location) => {
    setSelectedLocations(
      selectedLocations.some(loc => loc.name === location.name)
        ? selectedLocations.filter(loc => loc.name !== location.name)
        : [...selectedLocations, location]
    );
  };

  const handleSaveLocations = () => {
    if (selectedLocations.length === 0) return;
    saveGBPLocations(selectedLocations);
    setCurrentStep('connected');
    setTimeout(() => loadGBPConnectionStatus(), 1000);
  };

  const handleDisconnect = () => {
    disconnectGBP();
    setShowDisconnectConfirm(false);
    setCurrentStep('disconnected');
  };

  // ================================
  // RENDER HELPERS
  // ================================

  const renderLoading = () => (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Spinner className="size-4" />
      <span>Loading...</span>
    </div>
  );

  const renderDisconnectedState = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Connect your Google Business Profile to manage reviews and track ratings from your dashboard.
      </p>
      {oauthError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{oauthError}</AlertDescription>
        </Alert>
      )}
      <Button onClick={handleStartOAuth} disabled={oauthInitiating}>
        {oauthInitiating ? (
          <>
            <Spinner className="size-4 mr-2" />
            Connecting...
          </>
        ) : (
          <>
            <GoogleIcon />
            Connect with Google
          </>
        )}
      </Button>
    </div>
  );

  const renderLocationSelection = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {accountName ? `Signed in as ${accountName}.` : 'Connected to Google.'} Select the locations you want to manage.
      </p>

      {/* Account Selection (if multiple accounts) */}
      {accounts && accounts.length > 1 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Select account</p>
          {accounts.map((account) => (
            <button
              key={account.name}
              onClick={() => handleAccountSelect(account)}
              className={`w-full p-3 text-left border rounded-lg text-sm transition-colors ${
                selectedAccount?.name === account.name
                  ? 'border-primary/50 bg-primary/10'
                  : 'border-border hover:bg-muted/30'
              }`}
            >
              {account.accountName || account.name}
            </button>
          ))}
        </div>
      )}

      {/* Location Selection */}
      {selectedAccount && (
        <div className="space-y-3">
          {locationsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" />
              <span>Loading locations...</span>
            </div>
          ) : locations.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No locations found. Make sure you have a Google Business Profile set up.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-2">
                {locations.map((location) => (
                  <div
                    key={location.name}
                    className="flex items-start gap-3 p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => handleLocationToggle(location)}
                  >
                    <Checkbox
                      checked={selectedLocations.some(loc => loc.name === location.name)}
                      onCheckedChange={() => handleLocationToggle(location)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium text-foreground">{location.locationName}</p>
                        {location.verified && (
                          <CheckCircle2 className="h-4 w-4 text-green-500" title="Verified" />
                        )}
                      </div>
                      {location.address && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {location.address.addressLines?.[0]}, {location.address.locality}, {location.address.administrativeArea}
                        </p>
                      )}
                      {location.reviewStats && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                          <span>{location.reviewStats.averageRating || 0} ({location.reviewStats.totalReviewCount || 0} reviews)</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveLocations}
                  disabled={selectedLocations.length === 0 || savingLocations}
                >
                  {savingLocations ? (
                    <>
                      <Spinner className="size-4 mr-2" />
                      Saving...
                    </>
                  ) : (
                    `Save ${selectedLocations.length} Location${selectedLocations.length !== 1 ? 's' : ''}`
                  )}
                </Button>
                <Button variant="outline" onClick={() => setCurrentStep('disconnected')}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderConnectedState = () => (
    <div className="space-y-3">
      {/* Token Expired Warning */}
      {tokenExpired && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your Google Business Profile connection has expired. Please reconnect to continue managing reviews.
          </AlertDescription>
        </Alert>
      )}

      {/* Connected row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
          <span className="text-sm text-foreground">{accountName || 'Google Business Profile'}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDisconnectConfirm(true)}
          disabled={disconnecting}
        >
          {disconnecting ? 'Disconnecting...' : 'Disconnect'}
        </Button>
      </div>

      {/* Inline disconnect confirm */}
      {showDisconnectConfirm && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">Disconnect this account?</span>
          <button onClick={handleDisconnect} className="text-destructive hover:underline">Yes, disconnect</button>
          <button onClick={() => setShowDisconnectConfirm(false)} className="text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      )}

      {/* Connected Locations */}
      {connectedLocations.length > 0 && (
        <div className="space-y-1 pt-1">
          {connectedLocations.map((location) => (
            <div key={location.name} className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span>{location.locationName}</span>
              {location.reviewStats && (
                <>
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 ml-1" />
                  <span className="text-xs">{location.reviewStats.averageRating || 0} ({location.reviewStats.totalReviewCount || 0})</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ================================
  // MAIN RENDER
  // ================================

  return (
    <div className="max-w-3xl">
      {currentStep === 'loading' && renderLoading()}
      {currentStep === 'disconnected' && renderDisconnectedState()}
      {currentStep === 'select_locations' && renderLocationSelection()}
      {currentStep === 'connected' && renderConnectedState()}
    </div>
  );
};

export default GBPSettings;
